import React, { useState } from 'react';
import { Bell, Menu, Clock, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationDropdown from '../ui/NotificationDropdown';
import CartIcon from '../cart/CartIcon';

interface HeaderProps {
  onMenuClick: () => void;
  onToggleSidebar: () => void;
  sidebarVisible: boolean;
}

function Header({ onMenuClick, onToggleSidebar, sidebarVisible }: HeaderProps) {
  const { notifications } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const unreadCount = notifications.filter(n => !n.read).length;

  // Mettre à jour l'heure toutes les secondes
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative z-10 flex-shrink-0 flex h-16 glass-card border-b border-gray-200/50">
      {/* Mobile menu button */}
      <button
        type="button"
        className="px-4 border-r border-gray-200/50 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden hover:bg-gray-50/50 transition-colors"
        onClick={onMenuClick}
      >
        <span className="sr-only">Open sidebar</span>
        <Menu className="h-6 w-6" />
      </button>

      {/* Desktop sidebar toggle */}
      <button
        type="button"
        className="hidden md:flex px-4 border-r border-gray-200/50 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 hover:bg-gray-50/50 transition-colors items-center"
        onClick={onToggleSidebar}
        title={sidebarVisible ? 'Masquer la sidebar' : 'Afficher la sidebar'}
      >
        {sidebarVisible ? (
          <PanelLeftClose className="h-5 w-5" />
        ) : (
          <PanelLeft className="h-5 w-5" />
        )}
      </button>
      <div className="flex-1 px-4 flex justify-between items-center">
        {/* Date et heure */}
        <div className="flex-1 flex items-center">
          <div className="flex items-center space-x-6">
            <div className="text-sm font-medium text-gray-700 bg-gradient-to-r from-blue-50 to-orange-50 px-4 py-2 rounded-full border border-gray-200/50">
              {new Intl.DateTimeFormat('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }).format(currentTime)}
            </div>
            <div className="flex items-center text-sm font-semibold text-gray-900 bg-white/80 px-3 py-2 rounded-lg shadow-sm border border-gray-200/50">
              <Clock className="h-4 w-4 mr-2 text-orange-500" />
              {currentTime.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>
          </div>
        </div>

        {/* Right section */}
        <div className="ml-4 flex items-center space-x-4">
          {/* Panier */}
          <CartIcon />
          
          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 hover:shadow-lg"
            >
              <Bell className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <NotificationDropdown onClose={() => setShowNotifications(false)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;